import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Car, CheckCircle, XCircle, Wifi, WifiOff, Send, MapPin, Star, AlertTriangle, Phone, MessageSquare, DollarSign, FileText, TrendingUp, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import MapView from "@/components/chauffeur/MapView";
import useChauffeurSocket from "@/hooks/useChauffeurSocket";
import DriverRulesModal from "@/components/chauffeur/DriverRulesModal";
import RatingModal from "@/components/chauffeur/RatingModal";
import DriverHeatmap from "@/components/chauffeur/DriverHeatmap";
import { RoutePolyline, RouteSummary } from "@/components/chauffeur/RouteOverlay";
import PriorityCard from "@/components/chauffeur/PriorityCard";
import MagnetCard from "@/components/chauffeur/MagnetCard";
import HazardLayer from "@/components/chauffeur/HazardLayer";
import HazardReportButton from "@/components/chauffeur/HazardReportButton";
import WaypointConfirmCard from "@/components/chauffeur/WaypointConfirmCard";
import PickupPinEntry from "@/components/chauffeur/PickupPinEntry";
import RideChat from "@/components/chauffeur/RideChat";
import { errorMessage } from "@/lib/errors";

const MODE_LABEL = { taxi: "Taxi", delivery: "Delivery" };

const KINGSTON = { lat: 18.0179, lng: -76.8099 }; // last-resort sentinel only

export default function DriverDashboard() {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  // Worldwide-default — set to (0,0) initially; the GPS ping below will
  // overwrite it the moment the driver goes online. Falls back to Kingston
  // only if no driver location is known yet.
  const [myLoc, setMyLoc] = useState({ lat: KINGSTON.lat, lng: KINGSTON.lng });
  const [applying, setApplying] = useState(false);
  const [app, setApp] = useState({ license_plate: "", vehicle_make: "", vehicle_model: "", vehicle_color: "", vehicle_year: "" });
  const [earnings, setEarnings] = useState(null);
  const [commissionPct, setCommissionPct] = useState(7);
  const [showRules, setShowRules] = useState(false);
  const [pendingRating, setPendingRating] = useState(null);
  const [priorityOffers, setPriorityOffers] = useState([]); // {ride, expires_at, rank}
  const [waypointPending, setWaypointPending] = useState(null); // {ride_id, waypoint, new_total, ...}
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const pingRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [me, settings] = await Promise.all([
        api.get("/chauffeur/drivers/me"),
        api.get("/chauffeur/platform-settings").catch(() => ({ data: { commission_percent: 7 } })),
      ]);
      setDriver(me.data);
      if (me.data.current_lat) setMyLoc({ lat: me.data.current_lat, lng: me.data.current_lng });
      setCommissionPct(settings.data?.commission_percent ?? 7);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Please sign in first"); window.location.href = "/vip"; return;
      }
      setDriver(null);
    } finally {
      setLoading(false);
    }
  };
  const loadQueue = async () => {
    try { const res = await api.get("/chauffeur/drivers/me/queue"); setQueue(res.data || []); } catch { /* */ }
  };
  const loadActive = async () => {
    try { const res = await api.get("/chauffeur/drivers/me/active"); setActiveRide(res.data); } catch { /* */ }
  };
  const loadEarnings = async () => {
    try { const res = await api.get("/chauffeur/drivers/me/earnings"); setEarnings(res.data); } catch { /* */ }
  };

  useEffect(() => { load(); }, []);
  // Best-effort geolocation on mount — gets the driver onto their real
  // location worldwide before the online-pin loop kicks in.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setMyLoc({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { enableHighAccuracy: false, timeout: 4000 },
    );
  }, []);
  useEffect(() => {
    if (!driver) return;
    loadEarnings();
  }, [driver?.user_email]); // eslint-disable-line
  useEffect(() => {
    if (!driver || driver.online_status !== "online") return;
    loadQueue(); loadActive();
    const t = setInterval(() => { loadQueue(); loadActive(); }, 6000);
    return () => clearInterval(t);
  }, [driver?.online_status, JSON.stringify(driver?.service_modes || [])]); // eslint-disable-line

  // GPS ping every 5s while online
  useEffect(() => {
    if (!driver || driver.online_status !== "online" || !navigator.geolocation) return;
    const ping = () => {
      navigator.geolocation.getCurrentPosition(
        async (p) => {
          const lat = p.coords.latitude, lng = p.coords.longitude;
          setMyLoc({ lat, lng });
          try { await api.post("/chauffeur/drivers/me/location", { lat, lng }); } catch { /* */ }
        },
        () => {}, { enableHighAccuracy: true, timeout: 4000, maximumAge: 3000 },
      );
    };
    ping();
    pingRef.current = setInterval(ping, 5000);
    return () => clearInterval(pingRef.current);
  }, [driver?.online_status]);

  const submitApp = async () => {
    if (!app.license_plate || !app.vehicle_make || !app.vehicle_model) { toast.error("Fill plate, make and model"); return; }
    setApplying(true);
    try {
      const res = await api.post("/chauffeur/drivers/apply", {
        ...app,
        vehicle_year: app.vehicle_year ? Number(app.vehicle_year) : null,
      });
      setDriver(res.data); toast.success("Application submitted — awaiting admin approval");
    } catch (err) {
      toast.error(errorMessage(err, "Application failed"));
    } finally { setApplying(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.post("/chauffeur/drivers/me/status", { status });
      setDriver((d) => ({ ...d, online_status: status }));
      toast.success(`You are now ${status}`);
    } catch (err) { toast.error(errorMessage(err, "Status update failed")); }
  };

  const acceptRide = async (rideId) => {
    try {
      const res = await api.post(`/chauffeur/rides/${rideId}/accept`);
      setActiveRide(res.data); setQueue([]); toast.success("Ride accepted!");
    } catch (err) { toast.error(errorMessage(err, "Accept failed")); }
  };

  const sendOffer = async (rideId, fare) => {
    try {
      await api.post(`/chauffeur/rides/${rideId}/offers`, { fare: Number(fare), eta_minutes: 8 });
      toast.success(`Offered JMD ${fare}`);
      setQueue((q) => q.map((r) => r.id === rideId ? { ...r, _my_offer: Number(fare) } : r));
    } catch (err) { toast.error(errorMessage(err, "Offer failed")); }
  };

  // Socket.IO: subscribe to new ride requests + my offer outcomes
  const { connected, on } = useChauffeurSocket({
    asDriver: driver?.online_status === "online",
    modes: driver?.service_modes,
  });

  const setServiceModes = async (next) => {
    if (!next || next.length === 0) { toast.error("Pick at least one mode"); return; }
    try {
      const res = await api.patch("/chauffeur/drivers/me/service-modes", { service_modes: next });
      setDriver((d) => (d ? { ...d, service_modes: res.data.service_modes } : d));
      toast.success(`Now serving: ${res.data.service_modes.map((m) => MODE_LABEL[m]).join(" + ")}`);
    } catch (err) { toast.error(errorMessage(err, "Update failed")); }
  };
  const toggleMode = (m) => {
    const cur = new Set(driver?.service_modes || ["taxi", "delivery"]);
    cur.has(m) ? cur.delete(m) : cur.add(m);
    setServiceModes(Array.from(cur));
  };

  useEffect(() => {
    if (!driver || driver.online_status !== "online") return;
    const off1 = on("new_ride_request", (ride) => {
      setQueue((q) => {
        if (q.some((r) => r.id === ride.id)) return q;
        return [ride, ...q].slice(0, 10);
      });
      toast.message(`New ${ride.type === "food" ? "delivery" : "ride"} request`, { description: `JMD ${ride.passenger_offer || ride.estimated_fare} · ${ride.pickup?.address}` });
    });
    const off2 = on("offer_accepted", (payload) => {
      // If I was the accepted driver, the active-ride poll picks it up; either way refresh
      loadActive(); loadQueue();
    });
    const off3 = on("offer_rejected", (payload) => {
      // Only relevant to me if it was my offer
      setQueue((q) => q);
    });
    // Smart Priority Dispatch — show a high-emphasis card at the top
    const off4 = on("priority_dispatch", (payload) => {
      setPriorityOffers((cur) => {
        const exists = cur.find((p) => p.ride?.id === payload.ride?.id);
        return exists ? cur : [payload, ...cur];
      });
      toast.success(`Priority ride · #${payload.rank}`,
        { description: "First-look — accept within 15s" });
    });
    // Passenger added a mid-ride stop — one-tap confirm card
    const off5 = on("waypoint_added", (payload) => {
      if (payload.needs_driver_confirm) {
        setWaypointPending(payload);
        toast.warning("Passenger added a stop", { description: `New total JMD ${payload.new_total}` });
      } else {
        loadActive();
      }
    });
    const off6 = on("waypoint_resolved", () => {
      setWaypointPending(null);
      loadActive();
    });
    const off7 = on("waypoint_removed", () => { loadActive(); });
    const off8 = on("chat_message", (msg) => {
      // Driver bumps unread when chat panel is closed AND sender isn't them
      const myEmail = driver?.email;
      if (!chatOpen && msg.sender_email !== myEmail) {
        setUnreadChat((n) => n + 1);
        toast.message("Rider", { description: msg.body || msg.quick_reply_text || "(quick reply)" });
      }
    });
    return () => { off1(); off2(); off3(); off4(); off5(); off6(); off7(); off8(); };
  }, [driver?.online_status, driver?.email, on, chatOpen]); // eslint-disable-line

  const updateRide = async (status) => {
    if (!activeRide) return;
    try {
      const res = await api.post(`/chauffeur/rides/${activeRide.id}/status`, { status });
      setActiveRide(res.data);
      toast.success(`Status: ${status}`);
      if (status === "completed") {
        setPendingRating(res.data); // rate the passenger
      }
      if (["completed", "cancelled"].includes(status)) {
        setTimeout(() => { setActiveRide(null); load(); loadEarnings(); }, 1000);
      }
    } catch (err) { toast.error(errorMessage(err, "Update failed")); }
  };

  if (loading) return <div className="pt-32 min-h-screen bg-luxury-black flex items-center justify-center text-gold/60 font-body">Loading driver console…</div>;

  if (!driver) {
    return (
      <div className="pt-24 min-h-screen bg-luxury-black">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Become an Aristocrats Driver</p>
          <h1 className="font-heading text-3xl text-white mb-2">Driver Application</h1>
          <p className="text-white/50 font-body text-sm mb-6">Submit your vehicle details. Admin reviews each application before activation.</p>
          <div className="space-y-3">
            <Input data-testid="apply-plate" value={app.license_plate} onChange={(e) => setApp({ ...app, license_plate: e.target.value })}
              placeholder="License plate" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="apply-make" value={app.vehicle_make} onChange={(e) => setApp({ ...app, vehicle_make: e.target.value })}
              placeholder="Make (Toyota)" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="apply-model" value={app.vehicle_model} onChange={(e) => setApp({ ...app, vehicle_model: e.target.value })}
              placeholder="Model (Camry)" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="apply-color" value={app.vehicle_color} onChange={(e) => setApp({ ...app, vehicle_color: e.target.value })}
              placeholder="Color (Black)" className="bg-white/5 border-white/15 text-white" />
            <Input data-testid="apply-year" type="number" value={app.vehicle_year}
              onChange={(e) => setApp({ ...app, vehicle_year: e.target.value })}
              placeholder="Year (2022)" className="bg-white/5 border-white/15 text-white" />
            <button data-testid="apply-submit" disabled={applying} onClick={submitApp}
              className="w-full flex items-center justify-center gap-2 bg-gold text-luxury-black py-3 font-body text-sm uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-50">
              <Send size={14} /> {applying ? "Submitting…" : "Apply to Drive"}
            </button>
          </div>
          <p className="text-white/40 text-xs font-body mt-4">Driver payouts run on cash-on-completion now. Stripe Connect bank deposits coming soon.</p>
        </div>
      </div>
    );
  }

  if (driver.approval_status === "pending") {
    return (
      <div className="pt-32 min-h-screen bg-luxury-black flex items-center justify-center">
        <div className="text-center max-w-md">
          <Car className="mx-auto mb-3 text-gold/60" size={48} />
          <h2 className="font-heading text-2xl text-white mb-2">Application Pending</h2>
          <p className="text-white/50 font-body text-sm">Admin will approve your driver account shortly. We'll send a notification.</p>
        </div>
      </div>
    );
  }
  if (driver.approval_status === "rejected") {
    return (
      <div className="pt-32 min-h-screen bg-luxury-black flex items-center justify-center">
        <div className="text-center max-w-md">
          <XCircle className="mx-auto mb-3 text-red-400/60" size={48} />
          <h2 className="font-heading text-2xl text-white mb-2">Application Rejected</h2>
          <p className="text-white/50 font-body text-sm">Please contact admin@aristocrats.com for details.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="driver-page" className="pt-24 min-h-screen bg-luxury-black">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Driver Console</p>
            <h1 className="font-heading text-3xl text-white">{driver.user_name}</h1>
            <p className="text-white/50 text-xs font-body mt-1">
              {driver.vehicle_color} {driver.vehicle_make} {driver.vehicle_model} · <span className="text-gold">{driver.license_plate}</span> · ⭐ {driver.rating?.toFixed(1) || "5.0"} · {driver.total_rides} rides
            </p>
          </div>
          {driver.online_status === "online" ? (
            <button data-testid="go-offline-btn" onClick={() => setStatus("offline")}
              className="flex items-center gap-2 bg-green-500/15 text-green-400 px-4 py-2 text-xs font-body uppercase tracking-wider border border-green-500/30 hover:bg-green-500/25 transition-colors">
              <Wifi size={14} /> Online — tap to go offline
            </button>
          ) : (
            <button data-testid="go-online-btn" onClick={() => setStatus("online")}
              className="flex items-center gap-2 bg-white/5 text-white/60 px-4 py-2 text-xs font-body uppercase tracking-wider border border-white/15 hover:text-white transition-colors">
              <WifiOff size={14} /> Offline — go online
            </button>
          )}
        </motion.div>

        {/* Earnings + Mode toggle row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div data-testid="earnings-card" className="p-4 border border-gold/30 bg-gold/5 rounded-md">
            <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/80 mb-1 flex items-center gap-1"><TrendingUp size={10} /> Lifetime Earnings</p>
            <p className="font-heading text-2xl text-white">JMD {earnings?.lifetime_earnings?.toLocaleString() || "0"}</p>
            <p className="text-white/40 text-xs font-body mt-1">{driver.total_rides} completed · ⭐ {earnings?.rating?.toFixed(1) || driver.rating?.toFixed(1)}</p>
          </div>
          <div className="p-4 border border-white/10 bg-white/5 rounded-md">
            <p className="text-[10px] font-body uppercase tracking-[0.2em] text-white/60 mb-1">This Week</p>
            <p className="font-heading text-2xl text-white">JMD {earnings?.week_payout?.toLocaleString() || "0"}</p>
            <p className="text-white/40 text-xs font-body mt-1">{earnings?.week_rides || 0} rides · {commissionPct}% platform fee deducted</p>
          </div>
          <div className="p-4 border border-white/10 bg-white/5 rounded-md">
            <p className="text-[10px] font-body uppercase tracking-[0.2em] text-white/60 mb-2">Service Modes</p>
            <div className="flex gap-2">
              {["taxi", "delivery"].map((m) => {
                const on = (driver.service_modes || []).includes(m);
                return (
                  <button key={m} data-testid={`toggle-mode-${m}`} onClick={() => toggleMode(m)}
                    className={`flex-1 py-2 text-[11px] font-body uppercase tracking-wider border transition-colors ${on ? "bg-gold/15 text-gold border-gold/40" : "border-white/15 text-white/40 hover:text-white"}`}>
                    {MODE_LABEL[m]}
                  </button>
                );
              })}
            </div>
            <button data-testid="open-rules-from-dashboard" onClick={() => setShowRules(true)}
              className="mt-2 inline-flex items-center gap-1 text-[10px] font-body text-white/40 hover:text-white">
              <FileText size={10} /> View driver rules
            </button>
          </div>
        </div>

        {activeRide ? (
          <ActiveDriverRide ride={activeRide} myLoc={myLoc} onUpdate={updateRide}
            commissionPct={commissionPct}
            waypointPending={waypointPending}
            onWaypointConfirm={() => { setWaypointPending(null); loadActive(); }}
            myEmail={driver?.email}
            chatOpen={chatOpen}
            setChatOpen={(v) => { if (v) setUnreadChat(0); setChatOpen(v); }}
            unreadChat={unreadChat}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-3">Nearby Requests</p>
              <MagnetCard enabled={
                driver.online_status === "online" &&
                queue.length === 0 &&
                priorityOffers.length === 0 &&
                !activeRide
              } />
              {priorityOffers.length > 0 && (
                <div data-testid="priority-stack" className="space-y-3 mb-4">
                  {priorityOffers.map((p) => (
                    <PriorityCard key={p.ride?.id} payload={p}
                      onTakeRide={(rid) => setPriorityOffers((cur) => cur.filter((x) => x.ride?.id !== rid))}
                      onDismiss={() => setPriorityOffers((cur) => cur.filter((x) => x.ride?.id !== p.ride?.id))} />
                  ))}
                </div>
              )}
              {driver.online_status !== "online" ? (
                <div className="border border-dashed border-white/15 rounded-md p-12 text-center">
                  <p className="text-white/50 font-body text-sm">Go online to see ride requests</p>
                </div>
              ) : queue.length === 0 ? (
                <div className="border border-dashed border-white/15 rounded-md p-12 text-center">
                  <p className="text-white/50 font-body text-sm">No nearby requests right now — staying online…</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {queue.map((r) => (
                    <QueueCard key={r.id} ride={r} commissionPct={commissionPct} onAcceptAtOffer={(fare) => sendOffer(r.id, fare)} />
                  ))}
                </div>
              )}
              {/* Driver-side hazard report — drop a community pin where you are */}
              {driver.online_status === "online" && (
                <div data-testid="driver-hazard-strip" className="mt-4 p-3 border border-amber-500/20 bg-amber-500/5 rounded-md flex items-center justify-between gap-3">
                  <p className="text-[11px] text-amber-300/80">Spotted something on the road? Help other drivers.</p>
                  <HazardReportButton lat={myLoc.lat} lng={myLoc.lng} compact />
                </div>
              )}
            </div>
            <MapView pickup={myLoc} height={500} data-testid="driver-map">
              <HazardLayer center={myLoc} radiusKm={25} allowConfirm />
            </MapView>
          </div>
        )}

        {/* Earnings Heatmap (always rendered when driver is approved) */}
        <div className="mt-6">
          <DriverHeatmap />
        </div>
      </div>
      <DriverRulesModal open={showRules} onClose={() => setShowRules(false)}
        defaultAccepted={true} onAccept={() => setShowRules(false)} />
      {pendingRating && (
        <RatingModal ride={pendingRating} role="driver"
          onClose={() => setPendingRating(null)} onRated={() => setPendingRating(null)} />
      )}
    </div>
  );
}

function QueueCard({ ride, onAcceptAtOffer, commissionPct = 7 }) {
  const passengerOffer = ride.passenger_offer ?? ride.estimated_fare;
  const [counter, setCounter] = useState("");
  const [mode, setMode] = useState("idle"); // idle | counter | sent
  const fare = mode === "counter" && counter ? Number(counter) : passengerOffer;
  const yourPayout = Math.round(fare * (1 - commissionPct / 100));
  return (
    <div data-testid={`queue-${ride.id}`} className="p-4 border border-white/10 bg-white/5 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-body uppercase tracking-wider text-gold">{ride.type === "food" ? "Food Delivery" : "Taxi"}</span>
        <span className="text-white text-sm font-body"><span className="text-white/40">passenger:</span> <span className="text-gold font-heading">JMD {passengerOffer}</span></span>
      </div>
      <p className="text-white text-sm font-body"><MapPin size={12} className="inline mr-1 text-gold" /> {ride.pickup?.address}</p>
      <p className="text-white/60 text-xs font-body"><MapPin size={10} className="inline mr-1" /> {ride.dropoff?.address}</p>
      <p className="text-white/40 text-xs font-body mt-1">{ride.distance_km} km · system est. JMD {ride.estimated_fare}</p>
      <p data-testid={`payout-preview-${ride.id}`} className="text-green-400/80 text-[11px] font-body mt-1">You'll earn ~JMD {yourPayout} after {commissionPct}% platform fee</p>
      {ride._my_offer ? (
        <p data-testid={`my-offer-${ride.id}`} className="mt-3 text-xs font-body text-amber-300">Your offer JMD {ride._my_offer} sent — awaiting passenger</p>
      ) : mode === "idle" ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button data-testid={`accept-${ride.id}`} onClick={() => onAcceptAtOffer(passengerOffer)}
            className="bg-gold text-luxury-black py-2 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
            Accept @ JMD {passengerOffer}
          </button>
          <button data-testid={`counter-${ride.id}`} onClick={() => { setMode("counter"); setCounter(String(ride.estimated_fare)); }}
            className="border border-gold/30 text-gold py-2 text-xs font-body uppercase tracking-wider hover:bg-gold/10 transition-colors">
            <DollarSign size={12} className="inline" /> Counter
          </button>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Input data-testid={`counter-input-${ride.id}`} type="number" value={counter}
            onChange={(e) => setCounter(e.target.value)} placeholder="Your fare (JMD)"
            className="bg-luxury-black/60 border-gold/30 text-gold text-sm" />
          <button data-testid={`send-counter-${ride.id}`} onClick={() => onAcceptAtOffer(counter)}
            disabled={!counter || Number(counter) < 50}
            className="bg-gold text-luxury-black px-3 py-2 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors disabled:opacity-40">
            Send
          </button>
          <button onClick={() => setMode("idle")} className="text-white/40 hover:text-white px-2"><XCircle size={14} /></button>
        </div>
      )}
    </div>
  );
}

function ActiveDriverRide({ ride, myLoc, onUpdate, commissionPct = 7, waypointPending, onWaypointConfirm, myEmail, chatOpen, setChatOpen, unreadChat }) {
  const fare = ride.agreed_fare || ride.estimated_fare;
  const yourPayout = Math.round(fare * (1 - commissionPct / 100));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-3">
        <div className="p-5 border border-gold/30 bg-gold/5 rounded-md">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1">Active Ride</p>
              <p className="font-heading text-lg text-white">{ride.client_name}</p>
              <p className="text-white/60 text-sm font-body mt-2">{ride.pickup?.address}</p>
              <p className="text-white/40 text-xs font-body">→ {ride.dropoff?.address}</p>
              <p className="text-gold text-xs font-body uppercase tracking-wider mt-2">JMD {ride.agreed_fare || ride.estimated_fare} · {ride.distance_km} km</p>
              <p className="text-green-400/80 text-[11px] font-body mt-1">You'll earn JMD {yourPayout} after {commissionPct}% fee</p>
            </div>
            {ride.client_phone && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <a
                  data-testid="call-rider-btn"
                  href={`tel:${ride.client_phone}`}
                  aria-label="Call rider"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-luxury-black shadow-lg shadow-emerald-500/30 transition-all hover:scale-105"
                >
                  <Phone size={20} />
                </a>
                <a
                  data-testid="text-rider-btn"
                  href={`sms:${ride.client_phone}?&body=${encodeURIComponent(`Hi ${(ride.client_name || "").split(" ")[0] || "there"} — your driver here. `)}`}
                  aria-label="Text rider"
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-sky-500 hover:bg-sky-400 text-luxury-black shadow-lg shadow-sky-500/30 transition-all hover:scale-105"
                >
                  <MessageSquare size={20} />
                </a>
              </div>
            )}
          </div>
          {ride.client_phone && (
            <p className="text-[11px] text-white/45 mt-2">Phone = call · Text = SMS rider — native apps open.</p>
          )}
          {(ride.waypoints?.length || 0) > 0 && (
            <div data-testid="active-ride-stops" className="mt-3 pt-3 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-purple-300 mb-1.5">Stops on the way ({ride.waypoints.length})</p>
              <ol className="space-y-1">
                {ride.waypoints.map((w, i) => (
                  <li key={w.id || i} className="text-[11px] text-white/70 flex items-center gap-2">
                    <span className="text-purple-300 font-display">{i + 1}</span>
                    <span className="truncate">{w.address}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
        {/* Waypoint confirm card — passenger added a stop mid-ride */}
        {waypointPending && waypointPending.ride_id === ride.id && (
          <WaypointConfirmCard pending={waypointPending} onResolve={onWaypointConfirm} />
        )}
        {/* Quiet ride badge */}
        {ride.quiet_ride && (
          <div data-testid="quiet-ride-driver-badge"
            className="p-2 border border-purple-400/30 bg-purple-500/10 rounded text-[11px] text-purple-300 uppercase tracking-wider">
            🤫 Quiet ride preferred — please skip the small talk
          </div>
        )}
        {/* Pickup PIN entry — driver verifies they have the right rider */}
        <PickupPinEntry ride={ride} onVerified={onUpdate} />
        {/* In-app chat trigger + slide-up panel */}
        <button data-testid="driver-open-chat-btn" onClick={() => setChatOpen?.(true)}
          className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-xs font-body uppercase tracking-wider rounded border border-sky-500/30 text-sky-300 hover:bg-sky-500/10 transition-colors">
          <MessageSquare size={14} /> Chat with rider {unreadChat > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-sky-500 text-luxury-black text-[10px]">{unreadChat}</span>}
        </button>
        {chatOpen && (
          <RideChat ride={ride} myEmail={myEmail} onClose={() => setChatOpen?.(false)} />
        )}
        <RouteSummary rideId={ride.id} />
        <div className="grid grid-cols-2 gap-2">
          {ride.status === "accepted" && (
            <button data-testid="status-arriving" onClick={() => onUpdate("arriving")}
              className="col-span-2 bg-gold/20 text-gold py-3 text-xs font-body uppercase tracking-wider hover:bg-gold/30 transition-colors border border-gold/30">
              I'm Arriving
            </button>
          )}
          {ride.status === "arriving" && (
            <button data-testid="status-in-progress" onClick={() => onUpdate("in_progress")}
              className="col-span-2 bg-gold text-luxury-black py-3 text-xs font-body uppercase tracking-wider hover:bg-gold/90 transition-colors">
              Start Trip
            </button>
          )}
          {ride.status === "in_progress" && (
            <button data-testid="status-completed" onClick={() => onUpdate("completed")}
              className="col-span-2 bg-green-500/30 text-green-300 py-3 text-xs font-body uppercase tracking-wider border border-green-500/40 hover:bg-green-500/40 transition-colors">
              <CheckCircle size={14} className="inline mr-1" /> Complete Trip
            </button>
          )}
          <button data-testid="status-cancelled" onClick={() => { if (window.confirm("Cancel this ride?")) onUpdate("cancelled"); }}
            className="col-span-2 text-red-400/70 hover:text-red-400 py-2 text-xs font-body uppercase tracking-wider transition-colors">
            <XCircle size={12} className="inline mr-1" /> Cancel
          </button>
        </div>
      </div>
      <div className="lg:col-span-2">
        <MapView pickup={ride.pickup} dropoff={ride.dropoff} driver={myLoc}
          waypoints={ride.waypoints} recenter={myLoc} height={520}
          data-testid="driver-active-map">
          <RoutePolyline rideId={ride.id} />
          <HazardLayer center={myLoc} radiusKm={25} allowConfirm />
        </MapView>
        <div className="mt-2 flex items-center justify-end">
          <HazardReportButton lat={myLoc.lat} lng={myLoc.lng} compact />
        </div>
      </div>
    </div>
  );
}
